<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import draggable from 'vuedraggable'
import { useTeamsStore } from '@/stores/teams'
import { useAssignmentsStore } from '@/stores/assignments'
import { TeamStatus, TeamType, PersonnelStatus, AssignmentStatus } from '@/types'
import type { Personnel, Team } from '@/types'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'
import PersonnelTooltip from '@/components/personnel/PersonnelTooltip.vue'
import { useDragState } from '@/composables/useDragState'

const props = defineProps<{
  incidentId: string
  opId: string
  team: Team
  leader: Personnel | null
  members: Personnel[]
  canUpdateStatus: boolean
  searchQuery?: string
}>()

function isMatch(p: Personnel): boolean {
  const q = props.searchQuery?.trim().toLowerCase()
  if (!q) return false
  return (
    `${p.first} ${p.last}`.toLowerCase().includes(q) ||
    (p.agency ?? '').toLowerCase().includes(q)
  )
}

const emit = defineEmits<{
  debrief: []
  'view-details': []
}>()

const teamsStore = useTeamsStore()
const assignmentsStore = useAssignmentsStore()
const { startDrag, endDrag, isDragging } = useDragState()

//── Identifier inline edit ─────────────────────────────────────────────────

const editing = ref(false)
const localIdentifier = ref('')
const identifierInputRef = ref<HTMLInputElement | null>(null)

function startEditing() {
  editing.value = true
  localIdentifier.value = props.team.team_identifier
  nextTick(() => identifierInputRef.value?.focus())
}

function saveIdentifier() {
  editing.value = false
  const trimmed = localIdentifier.value.trim()
  if (trimmed && trimmed !== props.team.team_identifier) {
    teamsStore.updateTeamIdentifier(props.incidentId, props.opId, props.team.team_id, trimmed)
  }
}

function cancelEditing() {
  editing.value = false
  localIdentifier.value = props.team.team_identifier
}

// ── Status / type ──────────────────────────────────────────────────────────

const teamStatusOptions = [
  { label: 'Building', value: TeamStatus.Building },
  { label: 'Awaiting Assignment', value: TeamStatus.AwaitingAssignment },
  { label: 'Assigned', value: TeamStatus.Assigned },
  { label: 'Enroute', value: TeamStatus.Enroute },
  { label: 'In Assignment', value: TeamStatus.OnScene },
  { label: 'RTB', value: TeamStatus.RTB },
  { label: 'Returned', value: TeamStatus.Returned },
  { label: 'Emergency', value: TeamStatus.Emergency },
]

const teamTypeOptions = [
  { label: 'Ground', value: TeamType.Ground },
  { label: 'K9', value: TeamType.K9 },
  { label: 'Air', value: TeamType.Air },
  { label: 'Bike', value: TeamType.Bike },
  { label: 'Type 1', value: TeamType.Type1 },
  { label: 'Rescue', value: TeamType.Rescue },
]

const teamTypeOptionsAbbrev = [
  { label: 'GND', value: TeamType.Ground },
  { label: 'K9', value: TeamType.K9 },
  { label: 'AIR', value: TeamType.Air },
  { label: 'BIKE', value: TeamType.Bike },
  { label: 'T1', value: TeamType.Type1 },
  { label: 'RSC', value: TeamType.Rescue },
]

function onStatusChange(newStatus: TeamStatus) {
  teamsStore.updateTeamStatus(props.incidentId, props.opId, props.team.team_id, newStatus)
}

function onTypeChange(newType: TeamType) {
  teamsStore.updateTeamType(props.incidentId, props.opId, props.team.team_id, newType)
}

const typeTagClass = 'p-0 ml-1 leading-none flex items-center text-[12px] font-mono uppercase tracking-wide text-on-surface-variant cursor-pointer focus:outline-none hover:text-on-surface'

const memberCount = computed(() => props.members.length + (props.leader ? 1 : 0))

const activeLeader = computed(() => (props.leader?.time_out === null ? props.leader : null))
const checkedOutLeader = computed(() => (props.leader?.time_out != null ? props.leader : null))
const activeMembers = computed(() => props.members.filter((m) => m.time_out === null))
const checkedOutMembers = computed(() => props.members.filter((m) => m.time_out !== null))

const statusDotColor = computed(() => {
  const map: Record<string, string> = {
    [TeamStatus.Building]: 'bg-outline-variant',
    [TeamStatus.AwaitingAssignment]: 'bg-outline-variant',
    [TeamStatus.Assigned]: 'bg-violet-400',
    [TeamStatus.Enroute]: 'bg-blue-400',
    [TeamStatus.OnScene]: 'bg-green-500',
    [TeamStatus.RTB]: 'bg-amber-400',
    [TeamStatus.Returned]: 'bg-outline-variant',
    [TeamStatus.Emergency]: 'bg-red-500',
  }
  return map[props.team.team_status] ?? 'bg-outline-variant'
})

// ── Debrief ────────────────────────────────────────────────────────────────

const teamTAs = computed(() => assignmentsStore.getTeamAssignments(props.team.team_id))
const hasDebriefable = computed(() => teamTAs.value.length > 0)
const debriefedCount = computed(() => teamTAs.value.filter((ta) => Object.keys(ta.debrief_cols ?? {}).length > 0).length)
const allDebriefed = computed(() => hasDebriefable.value && debriefedCount.value === teamTAs.value.length)

const activeAssignments = computed(() => {
  const tas = assignmentsStore.getTeamAssignments(props.team.team_id)
  return tas
    .map((ta) => assignmentsStore.assignments.find((a) => a.assignment_id === ta.assignment_id))
    .filter((a): a is NonNullable<typeof a> => a != null)
})

// ── Drop zone appearance ───────────────────────────────────────────────────

const leaderZoneClass = computed(() => {
  const base = 'border border-dashed rounded-sm transition-colors'
  if (props.leader) return `${base} border-transparent`
  return isDragging.value
    ? `${base} border-primary/50 bg-primary/5`
    : `${base} border-transparent`
})

const membersZoneClass = computed(() => {
  const base = 'border border-dashed rounded-sm mt-1 transition-colors'
  if (activeMembers.value.length > 0) return `${base} border-transparent`
  return isDragging.value
    ? `${base} border-primary/50 bg-primary/5`
    : `${base} border-transparent`
})

// ── Drag and drop ──────────────────────────────────────────────────────────

const leaderArray = computed<Personnel[]>(() => (activeLeader.value ? [activeLeader.value] : []))

async function doAssignToTeam(person: Personnel) {
  await teamsStore.assignMemberToTeam(props.incidentId, props.opId, person.user_id, props.team.team_id, person.personnel_id)
  await updateDoc(
    doc(db, 'incidents', props.incidentId, 'operational_periods', props.opId, 'personnel', person.personnel_id),
    { team_id: props.team.team_id, status: PersonnelStatus.Assigned },
  )
}

async function handleLeaderZoneChange(event: { added?: { element: Personnel }; removed?: { element: Personnel } }) {
  if (event.removed) {
    if (props.team.team_leader_personnel_id === event.removed.element.personnel_id) {
      await teamsStore.setTeamLeader(props.incidentId, props.opId, props.team.team_id, null)
    }
  }
  if (event.added) {
    const person = event.added.element
    if (person.status === PersonnelStatus.OutOfService) return
    await teamsStore.setTeamLeader(props.incidentId, props.opId, props.team.team_id, person.personnel_id)
    if (person.team_id !== props.team.team_id) {
      await doAssignToTeam(person)
    }
  }
}

async function handleMembersZoneChange(event: { added?: { element: Personnel } }) {
  if (!event.added) return
  const person = event.added.element
  if (person.status === PersonnelStatus.OutOfService) return

  if (props.team.team_leader_personnel_id === person.personnel_id) {
    await teamsStore.setTeamLeader(props.incidentId, props.opId, props.team.team_id, null)
  }

  if (person.team_id !== props.team.team_id) {
    await doAssignToTeam(person)
  }
}
</script>

<template>
  <div
    class="@container border border-border bg-surface rounded-sm cursor-pointer overflow-hidden"
    :class="team.team_status === TeamStatus.Emergency ? 'animate-pulse border-red-500' : ''"
    @click="emit('view-details')"
  >
    <!-- Header: identifier · type · status -->
    <div class="flex items-center gap-1.5 px-2.5 pt-2 pb-2" @click.stop>
      <input
        v-if="editing"
        ref="identifierInputRef"
        v-model="localIdentifier"
        class="font-mono font-bold text-base w-16 border border-orange-500 rounded px-1 py-0 outline-none focus:ring-1 focus:ring-orange-400 bg-surface-container-high text-on-surface"
        @blur="saveIdentifier"
        @keydown.enter="saveIdentifier"
        @keydown.escape="cancelEditing"
      />
      <span
        v-else
        class="font-black font-headline text-base uppercase tracking-tight text-on-surface cursor-text hover:text-primary shrink-0"
        @click.stop="startEditing"
      >
        {{ team.team_identifier }}
      </span>

      <div class="hidden @[240px]:block">
        <FosSelect
          :model-value="team.team_type as string"
          :options="teamTypeOptions"
          :trigger-class="typeTagClass"
          icon-class="hidden"
          @update:model-value="(v) => onTypeChange(v as TeamType)"
        />
      </div>
      <div class="block @[240px]:hidden">
        <FosSelect
          :model-value="team.team_type as string"
          :options="teamTypeOptionsAbbrev"
          :trigger-class="typeTagClass"
          icon-class="hidden"
          @update:model-value="(v) => onTypeChange(v as TeamType)"
        />
      </div>

      <div class="ml-auto flex items-center gap-1 shrink-0">
        <span class="w-1.5 h-1.5 shrink-0" :class="statusDotColor" />
        <FosSelect
          :model-value="team.team_status as string"
          :options="teamStatusOptions"
          :disabled="!canUpdateStatus"
          trigger-class="p-0 leading-none flex items-center text-[11px] font-mono uppercase tracking-wide text-on-surface-variant cursor-pointer focus:outline-none hover:text-on-surface disabled:opacity-40 disabled:cursor-not-allowed"
          @update:model-value="(v) => onStatusChange(v as TeamStatus)"
        />
      </div>
    </div>

    <!-- Assignment band (only when assignments exist) -->
    <div
      v-if="activeAssignments.length"
      class="mx-2.5 mb-2 rounded-sm border px-2 py-1 flex flex-col gap-0.5"
      :class="activeAssignments.some(a => a.assignment_status === AssignmentStatus.InProgress)
        ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800'
        : 'bg-surface-container border-border'"
      @click.stop
    >
      <div
        v-for="a in activeAssignments"
        :key="a.assignment_id"
        class="flex items-center gap-1.5 min-w-0"
      >
        <span
          class="shrink-0 w-1.5 h-1.5 rounded-full"
          :class="a.assignment_status === AssignmentStatus.InProgress ? 'bg-blue-500' : 'bg-outline-variant'"
        />
        <span
          class="font-mono text-xs truncate"
          :class="a.assignment_status === AssignmentStatus.InProgress ? 'text-blue-800 dark:text-blue-300' : 'text-outline'"
        >{{ a.assignment_name }}</span>
        <span
          class="ml-auto shrink-0 font-mono text-[10px] uppercase"
          :class="a.assignment_status === AssignmentStatus.InProgress ? 'text-blue-500 dark:text-blue-400' : 'text-outline-variant'"
        >{{ a.assignment_status === AssignmentStatus.InProgress ? 'Active' : a.assignment_status }}</span>
      </div>
    </div>

    <!-- Personnel roster -->
    <div class="border-t border-border px-2.5 pt-2 pb-2" @click.stop>
      <div class="flex items-center justify-between mb-.25">
        <span class="text-[10px] font-mono uppercase tracking-widest text-outline">Lead</span>
        <span class="text-[10px] font-mono text-outline-variant">{{ memberCount }} mbr</span>
      </div>

      <!-- Leader drop zone -->
      <div :class="leaderZoneClass" class="relative">
        <draggable
          :model-value="leaderArray"
          :group="{ name: 'personnel' }"
          item-key="personnel_id"
          ghost-class="drag-ghost--leader"
          drag-class="drag-cursor--chip"
          :force-fallback="true"
          :fallback-on-body="true"
          class="flex items-center min-h-[34px] p-1"
          @start="startDrag"
          @end="endDrag"
          @change="(e: { added?: { element: Personnel }; removed?: { element: Personnel } }) => handleLeaderZoneChange(e)"
        >
          <template #item="{ element }">
            <div
              :data-person-name="`${element.first} ${element.last}`"
              class="inline-flex items-center gap-1 px-2 py-0.5 text-xs cursor-grab select-none rounded-sm border"
              :class="isMatch(element) ? 'bg-primary/10 border-primary/60 text-primary' : 'bg-surface-container border-border'"
            >
              <span class="text-[10px] font-bold leading-none" :class="isMatch(element) ? 'text-primary' : 'text-outline'">★</span>
              <PersonnelTooltip :person="element">
                <span :class="isMatch(element) ? 'text-primary' : 'text-on-surface-variant'">{{ element.first }} {{ element.last }}</span>
              </PersonnelTooltip>
            </div>
          </template>
        </draggable>
        <!-- Checked-out leader (static, non-draggable) -->
        <div v-if="checkedOutLeader && leaderArray.length === 0" class="flex items-center min-h-[34px] p-1">
          <div
            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-sm opacity-50 select-none cursor-default border"
            :class="isMatch(checkedOutLeader) ? 'bg-primary/10 border-primary/60' : 'bg-surface-container border-border'"
          >
            <span class="text-outline text-[10px] font-bold leading-none">★</span>
            <PersonnelTooltip :person="checkedOutLeader">
              <span class="text-on-surface-variant">{{ checkedOutLeader.first }} {{ checkedOutLeader.last }}</span>
            </PersonnelTooltip>
          </div>
        </div>
        <span
          v-if="isDragging && !leader"
          class="absolute inset-0 flex items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary/70 select-none pointer-events-none"
        ><span class="font-bold leading-none">★</span>Lead</span>
      </div>

      <!-- Members label -->
      <div class="mt-.5 mb-.25">
        <span class="text-[10px] font-mono uppercase tracking-widest text-outline">Members</span>
      </div>

      <!-- Members drop zone -->
      <div :class="membersZoneClass">
        <draggable
          :model-value="activeMembers"
          :group="{ name: 'personnel' }"
          item-key="personnel_id"
          ghost-class="drag-ghost--chip"
          drag-class="drag-cursor--chip"
          :force-fallback="true"
          :fallback-on-body="true"
          class="flex flex-wrap gap-1 min-h-[34px] p-1"
          @start="startDrag"
          @end="endDrag"
          @change="(e: { added?: { element: Personnel } }) => handleMembersZoneChange(e)"
        >
          <template #item="{ element }">
            <div
              :data-person-name="`${element.first} ${element.last}`"
              class="flex items-center px-2 py-0.5 text-xs cursor-grab select-none hover:border-outline-variant rounded-sm border"
              :class="isMatch(element) ? 'bg-primary/10 border-primary/60 text-primary' : 'bg-surface-container border-border'"
            >
              <PersonnelTooltip :person="element">
                <span :class="isMatch(element) ? 'text-primary' : 'text-on-surface-variant'">{{ element.first }} {{ element.last }}</span>
              </PersonnelTooltip>
            </div>
          </template>
        </draggable>
      </div>

      <!-- Checked-out members (static, non-draggable) -->
      <div v-if="checkedOutMembers.length > 0" class="flex flex-wrap gap-1 px-1 pt-1">
        <div
          v-for="m in checkedOutMembers"
          :key="m.personnel_id"
          class="flex items-center px-2 py-0.5 text-xs rounded-sm opacity-50 select-none cursor-default border"
          :class="isMatch(m) ? 'bg-primary/10 border-primary/60' : 'bg-surface-container border-border'"
        >
          <PersonnelTooltip :person="m">
            <span class="text-on-surface-variant">{{ m.first }} {{ m.last }}</span>
          </PersonnelTooltip>
        </div>
      </div>
    </div>

    <!-- Debrief footer -->
    <div v-if="hasDebriefable" class="border-t border-border px-2.5 py-1.5" @click.stop>
      <FosButton
        :variant="allDebriefed ? 'tertiary' : 'secondary'"
        size="sm"
        class="w-full"
        @click.stop="emit('debrief')"
      >
        <span v-if="allDebriefed" class="material-symbols-outlined text-[14px]">check_circle</span>
        <span v-else-if="debriefedCount > 0" class="font-mono text-[10px]">{{ debriefedCount }}/{{ teamTAs.length }}</span>
        {{ allDebriefed ? 'Debriefed' : 'Debrief' }}
      </FosButton>
    </div>
  </div>
</template>
